import {
  Bucket,
  BucketId,
  Container,
  ContainerId,
  ExperimentConfig,
  ExperimentKey,
  InAppMessageConfig,
  InAppMessageKey,
  ParameterConfiguration,
  ParameterConfigurationId,
  Properties,
  RemoteConfigParameterConfig,
  RemoteConfigParameterKey,
  Segment,
  SegmentKey
} from "../../model/model"
import { Attributes, WorkspaceConfigDto } from "../dto"
import CollectionUtil from "../../util/CollectionUtil"
import Workspace, { WorkspaceMetadata } from "../Workspace"
import { PropertiesBuilder } from "../../../../hackle/property/PropertiesBuilder"
import ObjectUtil from "../../util/ObjectUtil"

/**
 * Workspace-config metadata. `modifiedAt` is the Last-Modified value the config was fetched with.
 */
export interface WorkspaceConfigMetadata extends WorkspaceMetadata {
  modifiedAt: string | null
}

export default class WorkspaceConfig implements Workspace {
  private readonly experiments: Map<ExperimentKey, ExperimentConfig>
  private readonly splitUrlExperiments: Map<ExperimentKey, ExperimentConfig>
  private readonly featureFlags: Map<ExperimentKey, ExperimentConfig>
  private readonly buckets: Map<BucketId, Bucket>
  private readonly segments: Map<SegmentKey, Segment>
  private readonly containers: Map<ContainerId, Container>
  private readonly parameterConfigurations: Map<ParameterConfigurationId, ParameterConfiguration>
  private readonly remoteConfigParameters: Map<RemoteConfigParameterKey, RemoteConfigParameterConfig>
  private readonly inAppMessages: Map<InAppMessageKey, InAppMessageConfig>
  private readonly attributes: Attributes
  readonly metadata: WorkspaceConfigMetadata

  constructor(
    experiments: ExperimentConfig[],
    splitUrlExperiments: ExperimentConfig[],
    featureFlags: ExperimentConfig[],
    buckets: Bucket[],
    segments: Segment[],
    containers: Container[],
    parameterConfigurations: ParameterConfiguration[],
    remoteConfigParameters: RemoteConfigParameterConfig[],
    inAppMessages: InAppMessageConfig[],
    attributes: Attributes,
    metadata: WorkspaceConfigMetadata
  ) {
    this.experiments = CollectionUtil.associateBy(experiments, (it) => it.key)
    this.splitUrlExperiments = CollectionUtil.associateBy(splitUrlExperiments, (it) => it.key)
    this.featureFlags = CollectionUtil.associateBy(featureFlags, (it) => it.key)
    this.buckets = CollectionUtil.associateBy(buckets, (it) => it.id)
    this.segments = CollectionUtil.associateBy(segments, (it) => it.key)
    this.containers = CollectionUtil.associateBy(containers, (it) => it.id)
    this.parameterConfigurations = CollectionUtil.associateBy(parameterConfigurations, (it) => it.id)
    this.remoteConfigParameters = CollectionUtil.associateBy(remoteConfigParameters, (it) => it.key)
    this.inAppMessages = CollectionUtil.associateBy(inAppMessages, (it) => it.key)
    this.attributes = attributes
    this.metadata = metadata
  }

  getExperiments() {
    return Array.from(this.experiments.values())
  }

  getSplitUrlExperiments(): ExperimentConfig[] {
    return Array.from(this.splitUrlExperiments.values())
  }

  getFeatureFlags() {
    return Array.from(this.featureFlags.values())
  }

  getRemoteConfigParameters(): RemoteConfigParameterConfig[] {
    return Array.from(this.remoteConfigParameters.values())
  }

  getInAppMessages(): InAppMessageConfig[] {
    return Array.from(this.inAppMessages.values())
  }

  getExperimentOrNull(experimentKey: ExperimentKey): ExperimentConfig | undefined {
    return this.experiments.get(experimentKey)
  }

  getFeatureFlagOrNull(featureKey: ExperimentKey): ExperimentConfig | undefined {
    return this.featureFlags.get(featureKey)
  }

  getBucketOrNull(bucketId: BucketId): Bucket | undefined {
    return this.buckets.get(bucketId)
  }

  getSegmentOrNull(segmentKey: SegmentKey): Segment | undefined {
    return this.segments.get(segmentKey)
  }

  getContainerOrNull(containerId: ContainerId): Container | undefined {
    return this.containers.get(containerId)
  }

  getParameterConfigurationOrNull(
    parameterConfigurationId: ParameterConfigurationId
  ): ParameterConfiguration | undefined {
    return this.parameterConfigurations.get(parameterConfigurationId)
  }

  getRemoteConfigParameterOrNull(remoteConfigParameterKey: string): RemoteConfigParameterConfig | undefined {
    return this.remoteConfigParameters.get(remoteConfigParameterKey)
  }

  getInAppMessageOrNull(messageKey: InAppMessageKey): InAppMessageConfig | undefined {
    return this.inAppMessages.get(messageKey)
  }

  getAttributes() {
    return this.attributes
  }

  toProperties(): Properties {
    const properties = new PropertiesBuilder()
      .add("config_modified_at", this.metadata.modifiedAt)
      .add("evaluation_mode", "LOCAL")
      .build()
    return ObjectUtil.fromMap(properties)
  }

  static from(dto: WorkspaceConfigDto, modifiedAt: string | null = null): WorkspaceConfig {
    const parameterConfigurations = dto.parameterConfigurations.map(ParameterConfiguration.fromJson)

    // Experiments are ordered by the server-assigned order so the list accessors iterate by priority.
    const experiments = CollectionUtil.mapNotNullOrUndefined(dto.experiments, (it) =>
      ExperimentConfig.fromJson("AB_TEST", it, parameterConfigurations)
    )
    experiments.sort((a, b) => a.order - b.order)

    const splitUrlExperiments = CollectionUtil.mapNotNullOrUndefined(dto.experiments, (it) =>
      ExperimentConfig.fromJson("AB_TEST", it, parameterConfigurations)
    ).filter((experiment) => experiment.implementationType === "SPLIT_URL")
    splitUrlExperiments.sort((a, b) => a.order - b.order)

    const featureFlags = CollectionUtil.mapNotNullOrUndefined(dto.featureFlags, (it) =>
      ExperimentConfig.fromJson("FEATURE_FLAG", it, parameterConfigurations)
    )
    featureFlags.sort((a, b) => a.order - b.order)

    const buckets = dto.buckets.map(Bucket.fromJson)
    const segments = CollectionUtil.mapNotNullOrUndefined(dto.segments, Segment.fromJson)
    const containers = dto.containers.map(Container.fromJson)
    const remoteConfigParameters = CollectionUtil.mapNotNullOrUndefined(
      dto.remoteConfigParameters,
      RemoteConfigParameterConfig.fromJson
    )
    const inAppMessages = CollectionUtil.mapNotNullOrUndefined(dto.inAppMessages, InAppMessageConfig.fromJson)
    // In-app messages are ordered by the server-assigned order so getInAppMessages() iterates by priority.
    inAppMessages.sort((a, b) => a.order - b.order)

    const attributes = dto.workspace.attributes
    const metadata: WorkspaceConfigMetadata = {
      id: dto.workspace.id,
      environmentId: dto.workspace.environment.id,
      modifiedAt: modifiedAt
    }
    return new WorkspaceConfig(
      experiments,
      splitUrlExperiments,
      featureFlags,
      buckets,
      segments,
      containers,
      parameterConfigurations,
      remoteConfigParameters,
      inAppMessages,
      attributes,
      metadata
    )
  }
}
